import { Component, EventEmitter, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { ArticleUtils } from '@article/util/article-utils';
import { McsGdsDataService } from '@project/components/article/service/mcs-gds-data.service';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { LocaleUtils } from '@shared/util/locale-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { lastValueFrom, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-change-base-article',
  templateUrl: './change-base-article.component.html',
  styleUrls: ['./change-base-article.component.scss']
})
export class ChangeBaseArticleComponent {

  @Output() public finish = new EventEmitter<string | null>();

  public inputChanged$ = new Subject<string>()
  public inputValue: string = "";

  public currentBaseArticleNo?: string = "";
  public currentBaseArticle?: ArticleVO;
  public newBaseArticle?: ArticleVO;
  public newBaseArticleImage: string = "";
  public loading: boolean = true;
  public seriesList: string[] = [];
  public showUnsupportedSeriesHint = false;

  constructor(
    private articleManager: ArticleManager,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private bsModalRef: BsModalRef,
    private projectContentPresenter: ProjectContentPresenter,
    private mcsGdsDataService: McsGdsDataService,
    private alertHelper: AlertHelper,
  ) {
    const searchTriggered = this.inputChanged$.pipe(debounceTime(400));
    this.currentBaseArticle = this.projectModel.baseArticle;
    this.setNewBaseArticle(this.currentBaseArticle);

    this.currentBaseArticleNo = this.currentBaseArticle?.articleNo ?? this.projectModel.currentProject?.infoProperties.getString("mcsGdsInitArticleVersion");

    searchTriggered.pipe(takeUntilDestroyed()).subscribe(async input => {
      input = input.trim();
      if(input.length >= 3) {
        this.loading = true;
        this.setNewBaseArticle(await lastValueFrom(this.articleManager.getArticleByArticleNo(input)));
        this.loading = false;
      } else if (input.length == 0) {
        this.setNewBaseArticle(this.currentBaseArticle);
        this.showUnsupportedSeriesHint = false;
      }
    })
  }

  async ngOnInit(): Promise<void> {
    let projectType = this.projectModel.currentProjectType ?? '';
    this.loading = true;
    if(projectType !== 'MCS' && projectType !== 'GDS') {
      this.alertHelper.showError('global.label.error' ,'changeBaseArticle.error.not_available', {translationParams: [projectType]})
        .then(() => this.close());
      return;
    }
    this.seriesList = await lastValueFrom(this.mcsGdsDataService.getSeriesListByType(projectType));
    this.loading = false;
  }

  private setNewBaseArticle(article: ArticleVO | undefined): void {
    this.newBaseArticle = article;
    if(this.newBaseArticle) {
      const series = ArticleUtils.getArticleSeries(this.newBaseArticle.articleNo);
      this.showUnsupportedSeriesHint = this.seriesList.length > 0 && !this.seriesList.includes(series);
      this.newBaseArticleImage = ArticlePreviewUtils.getPreviewImageByNo(this.newBaseArticle.articleNo)
    } else {
      this.showUnsupportedSeriesHint = false;
    }
  }

  public separatedDisplayName(article: ArticleVO): [string, string] {
    const displayName = LocaleUtils.getValueByLocaleChain(article.displayName, this.localeResourceManager.getLocaleChain());
    return ArticleUtils.separatedDisplayName(displayName);
  }

  submit(): void {
    if (this.loading || !Log.assertExists(this.projectModel.currentProject)) return;
    this.projectModel.currentProject.infoProperties.setString("mcsGdsInitArticleNo", this.inputValue.trim());

    if(this.newBaseArticle) {
      this.projectModel.currentProject.infoProperties.setInt("mcsGdsInitArticleId", this.newBaseArticle.identity);
      this.projectModel.currentProject.infoProperties.setInt("mcsGdsInitArticleVersion", this.newBaseArticle.version);

    } else {
      this.projectModel.currentProject.infoProperties.remove("mcsGdsInitArticleId");
      this.projectModel.currentProject.infoProperties.remove("mcsGdsInitArticleVersion");
    }

    this.projectModel.baseArticle = this.newBaseArticle;
    this.projectModel.notifyCurrentProjectChanged();
    this.projectContentPresenter.rebuildProjectTree();

    this.close();
  }

  close(): void {
    this.bsModalRef.hide();
    this.finish.emit(this.inputValue.trim());
  }
}
